import React from "react";
import { CHART_FIXED_HEIGHT, CHART_WIDGET_HEIGHT } from "./aiUsageChartUtils";

const Pulse = ({ className = "", style }) => (
  <div
    className={`animate-pulse rounded-md bg-gray-200/80 ${className}`}
    style={style}
  />
);

export const AiWalletBalanceSkeleton = () => (
  <div className="h-11 flex items-center">
    <Pulse className="h-10 w-40 rounded-lg" />
  </div>
);

export const AiWalletCardSkeleton = () => (
  <div className="lg:col-span-2 rounded-xl border border-blue-200 bg-gradient p-6 text-white shadow-md flex flex-col">
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0 flex-1 space-y-3">
        <Pulse className="h-4 w-36 bg-white/25" />
        <AiWalletBalanceSkeleton />
        <div className="space-y-2 max-w-lg pt-1">
          <Pulse className="h-3 w-full bg-white/20" />
          <Pulse className="h-3 w-[92%] bg-white/20" />
          <Pulse className="h-3 w-[80%] bg-white/20" />
        </div>
      </div>
      <Pulse className="h-10 w-24 shrink-0 rounded-lg bg-white/30" />
    </div>
  </div>
);

export const AiPeriodSummaryMetricsSkeleton = () => (
  <div className="space-y-3">
    <div>
      <Pulse className="h-3 w-12" />
      <Pulse className="mt-2 h-8 w-24" />
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div className="rounded-lg bg-gray-50 px-3 py-2 space-y-2">
        <Pulse className="h-3 w-14" />
        <Pulse className="h-5 w-10" />
      </div>
      <div className="rounded-lg bg-gray-50 px-3 py-2 space-y-2">
        <Pulse className="h-3 w-16" />
        <Pulse className="h-5 w-12" />
      </div>
    </div>
  </div>
);

export const AiPeriodSummarySkeleton = () => (
  <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm flex flex-col">
    <Pulse className="h-3 w-28" />
    <div className="mt-1 min-h-[7.25rem] flex flex-col justify-center">
      <AiPeriodSummaryMetricsSkeleton />
    </div>
    <Pulse className="mt-1 h-10 w-full rounded-lg" />
  </div>
);

const ChartBarsSkeleton = () => (
  <div className="flex h-full items-end justify-between gap-3 px-2 pb-6 pt-4">
    {[72, 48, 88, 56, 64, 40, 76].map((height, index) => (
      <Pulse
        key={index}
        className="flex-1 max-w-[48px] rounded-t-md"
        style={{ height: `${height}%` }}
      />
    ))}
  </div>
);

const ChartAreaSkeleton = () => (
  <div className="relative h-full px-2 pb-6 pt-4">
    <Pulse className="absolute inset-x-2 bottom-6 top-8 rounded-lg opacity-60" />
    <div className="absolute inset-x-4 bottom-6 top-12 flex items-end">
      <svg
        className="h-full w-full text-gray-200"
        viewBox="0 0 320 120"
        preserveAspectRatio="none"
        aria-hidden
      >
        <path
          d="M0,90 C40,70 80,95 120,55 C160,20 200,75 240,40 C280,15 300,35 320,25 L320,120 L0,120 Z"
          fill="currentColor"
          className="animate-pulse"
        />
      </svg>
    </div>
  </div>
);

export const AiChartContentSkeleton = ({ variant = "bar" }) => (
  <>
    <div className="mb-3 flex flex-wrap gap-3 border-b border-gray-50 pb-3">
      <Pulse className="h-3 w-20" />
      <Pulse className="h-3 w-24" />
      <Pulse className="h-3 w-28" />
    </div>
    <div className="w-full" style={{ height: CHART_FIXED_HEIGHT }}>
      {variant === "area" ? <ChartAreaSkeleton /> : <ChartBarsSkeleton />}
    </div>
  </>
);

export const AiChartWidgetBodySkeleton = ({ variant = "bar" }) => (
  <div className="flex flex-1 flex-col px-4 pt-2 pb-4 sm:px-5">
    <AiChartContentSkeleton variant={variant} />
  </div>
);

export const AiChartWidgetSkeleton = ({ variant = "bar" }) => (
  <div
    className="flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
    style={{ height: CHART_WIDGET_HEIGHT }}
  >
    <div className="border-b border-gray-100 px-5 pt-4 pb-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <Pulse className="h-9 w-9 shrink-0 rounded-lg" />
          <div className="min-w-0 space-y-2 flex-1">
            <Pulse className="h-4 w-36" />
            <Pulse className="h-3 w-56 max-w-full" />
            <Pulse className="h-6 w-40 rounded-md" />
          </div>
        </div>
        <Pulse className="h-9 w-[220px] max-w-full rounded-lg" />
      </div>
    </div>
    <AiChartWidgetBodySkeleton variant={variant} />
  </div>
);

const USAGE_TABLE_SKELETON_ROWS = 8;

export const AiUsageTableBodySkeleton = () => (
  <>
    {Array.from({ length: USAGE_TABLE_SKELETON_ROWS }).map((_, rowIndex) => (
      <tr key={rowIndex}>
        <td className="px-4 py-3">
          <Pulse className="h-3 w-28" />
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <Pulse className="h-3 w-24" />
            <Pulse className="h-5 w-16 rounded-full" />
          </div>
        </td>
        <td className="px-4 py-3">
          <Pulse className="h-3 w-36" />
        </td>
        <td className="px-4 py-3">
          <Pulse className="h-3 w-14" />
        </td>
        <td className="px-4 py-3">
          <Pulse className="h-5 w-16 rounded-full" />
        </td>
      </tr>
    ))}
  </>
);

export const AiUsageHistoryTableSkeleton = () => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
    <div className="px-4 sm:px-5 py-4 border-b border-gray-100 flex items-center gap-2">
      <Pulse className="h-5 w-5 rounded" />
      <Pulse className="h-4 w-32" />
    </div>

    <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50/60">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div className="flex flex-col gap-4 min-w-0 flex-1 lg:flex-row lg:items-end lg:flex-wrap">
          <div className="min-w-0 space-y-2">
            <Pulse className="h-3 w-20" />
            <div className="flex flex-wrap gap-2">
              <Pulse className="h-8 w-24 rounded-full" />
              <Pulse className="h-8 w-28 rounded-full" />
              <Pulse className="h-8 w-32 rounded-full" />
            </div>
          </div>
          <div className="min-w-0 w-full sm:w-auto sm:min-w-[260px] space-y-2">
            <Pulse className="h-3 w-20" />
            <Pulse className="h-[38px] w-full rounded-md" />
          </div>
        </div>
        <Pulse className="h-[38px] w-28 rounded-md shrink-0" />
      </div>
    </div>

    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {["Date / time", "User", "Feature", "Cost (AUD)", "Status"].map(
              (_, index) => (
                <th key={index} className="px-4 py-3 text-left">
                  <Pulse className="h-3 w-16" />
                </th>
              )
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          <AiUsageTableBodySkeleton />
        </tbody>
      </table>
    </div>
  </div>
);

export const AiCreditsUsagePageSkeleton = () => (
  <>
    <div className="grid gap-4 lg:grid-cols-3 lg:items-stretch mb-6">
      <AiWalletCardSkeleton />
      <AiPeriodSummarySkeleton />
    </div>

    <div className="mb-6">
      <div className="mb-4 flex items-center gap-2">
        <Pulse className="h-5 w-5 rounded" />
        <div className="space-y-2">
          <Pulse className="h-4 w-36" />
          <Pulse className="h-3 w-72 max-w-full" />
        </div>
      </div>
      <div className="grid items-start gap-5 xl:grid-cols-2">
        <AiChartWidgetSkeleton variant="bar" />
        <AiChartWidgetSkeleton variant="area" />
      </div>
    </div>

    <div className="mb-6 rounded-xl border border-gray-200 bg-white px-5 py-4 flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <Pulse className="h-10 w-10 shrink-0 rounded-lg" />
        <div className="space-y-2 flex-1 min-w-0">
          <Pulse className="h-4 w-36" />
          <Pulse className="h-3 w-full max-w-md" />
        </div>
      </div>
      <Pulse className="h-9 w-32 rounded-lg shrink-0" />
    </div>

    <AiUsageHistoryTableSkeleton />
  </>
);
